import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import UserRolesMultiSelect from './UserRolesMultiSelect';
import { userRoleService } from '../../../services/userRoleService';
import { toast } from 'sonner';

const CreateUserModal = ({ isOpen, onClose, onCreateUser }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    roles: [],
    password: '',
    confirmPassword: '',
    countryId: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countries, setCountries] = useState([]);

  React.useEffect(() => {
    if (isOpen) {
      fetchCountries();
    }
  }, [isOpen]);

  const fetchCountries = async () => {
    try {
      const countriesData = await userRoleService?.getAllCountries();
      if (!countriesData?.length) {
        toast.error('Aucun pays disponible. Vérifiez la configuration des pays actifs.');
      }
      setCountries(countriesData);
    } catch (error) {
      console.error('Erreur lors du chargement des pays:', error);
      toast.error('Impossible de charger la liste des pays.');
      setCountries([]);
    }
  };

  const countryOptions = countries?.map(country => ({
    value: country?.id,
    label: country?.name || country?.country_name
  })) || [];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!formData?.countryId) {
      newErrors.countryId = 'Le pays est requis';
    }

    if (!formData?.roles?.length) {
      newErrors.roles = 'Sélectionnez au moins un rôle';
    }

    if (!formData?.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const userData = {
        name: formData?.name,
        email: formData?.email,
        roles: formData?.roles,
        password: formData?.password,
        countryId: formData?.countryId,
      };

      await onCreateUser(userData);

      setFormData({
        name: '',
        email: '',
        roles: [],
        password: '',
        confirmPassword: '',
        countryId: '',
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-md animate-slide-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-xl font-semibold text-foreground">Créer un nouvel utilisateur</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Nom complet"
            type="text"
            placeholder="Entrez le nom complet"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Input
            label="Adresse email"
            type="email"
            placeholder="utilisateur@exemple.com"
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            error={errors?.email}
            description="Utilisez un domaine email valide (ex: gmail.com, outlook.com) pour les tests"
            required
          />

          <Select
            label="Pays"
            options={countryOptions}
            value={formData?.countryId}
            onChange={(value) => handleChange('countryId', value)}
            error={errors?.countryId}
            placeholder="Sélectionnez un pays"
            required
          />

          <UserRolesMultiSelect
            value={formData?.roles}
            onChange={(roles) => handleChange('roles', roles)}
            error={errors?.roles}
            required
          />

          <Input
            label="Mot de passe initial"
            type="password"
            placeholder="Minimum 8 caractères"
            value={formData?.password}
            onChange={(e) => handleChange('password', e?.target?.value)}
            error={errors?.password}
            description="L'utilisateur sera invité à le changer lors de la première connexion"
            required
          />

          <Input
            label="Confirmer le mot de passe"
            type="password"
            placeholder="Ressaisissez le mot de passe"
            value={formData?.confirmPassword}
            onChange={(e) => handleChange('confirmPassword', e?.target?.value)}
            error={errors?.confirmPassword}
            required
          />

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isSubmitting}
              iconName="UserPlus"
              iconPosition="left"
              iconSize={18}
            >
              Créer l'utilisateur
            </Button>
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
